import { FC, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTranslation } from "react-i18next";
import {
  useVerification,
  VerificationState,
  PropertyVerificationResult,
} from "@services";

/** What the parent sends to the backend at create/save time. Null = nothing
 * verified/pending, so the property is created unverified. */
export interface VerificationPayload {
  permitNumber: string;
  idNumber?: string;
  ownerIdentityId?: number;
}

interface VerificationCardProps {
  /** Onboarding uses the standalone /verify; dashboard re-verifies a property. */
  surface: "onboarding" | "dashboard";
  /** Present on the dashboard for an existing property (re-verify + persist). */
  propertyId?: number;
  /** Reports the payload to persist at create-time (onboarding), or null. */
  onVerificationChange?: (payload: VerificationPayload | null) => void;
  /** Fired on a successful verification. */
  onVerified?: (result: PropertyVerificationResult) => void;
  /** Pre-fill the permit (re-verify / expired re-check from the edit page). */
  initialPermit?: string;
  /** Hide the card's own success block — the parent renders the verified state
   * itself (the edit section's موثّق block). */
  suppressSuccessView?: boolean;
}

const PERMIT_RE = /^\d{8}$/; // 8 digits (any leading number — MS-505 removed the "starts with 50" rule)
const digitsOnly = (v: string) => v.replace(/\D/g, "");
/** Client-side mask of a just-entered ID (•••• + last 4); never persisted raw. */
const maskLocal = (id: string) => (id.length > 4 ? `•••• ${id.slice(-4)}` : id);

const C = {
  card: "#F5F3FF",
  primary: "#5B3FFF",
  tagBg: "#EDE9FE",
  input: "#FFFFFF",
  border: "#E4E0F5",
  label: "#1F1B3A",
  muted: "#6B7280",
  successBg: "#F0FDF4",
  successBorder: "#BBF7D0",
  successText: "#16A34A",
  errorBg: "#FEF2F2",
  errorBorder: "#DC2626",
  errorText: "#DC2626",
  serviceBg: "#FEFCE8",
  serviceText: "#B45309",
};

type Phase = "form" | "loading" | "success";
type ApiAlert = null | "noMatch" | "service" | "inactivePermit";

export const VerificationCard: FC<VerificationCardProps> = ({
  surface,
  propertyId,
  onVerificationChange,
  onVerified,
  initialPermit = "",
  suppressSuccessView = false,
}) => {
  const { t } = useTranslation();
  const { getOwnersQuery, verifyIdentityMutation, verifyPropertyMutation } =
    useVerification();

  const { data: owners = [] } = getOwnersQuery();
  const savedOwner = owners[0]; // V1 individual host → a single roster owner
  const hasSaved = !!savedOwner;

  // "sameId" (use the saved roster owner) vs "newOwner" (enter a new National ID).
  const [mode, setMode] = useState<"sameId" | "newOwner">("sameId");
  const [ownerId, setOwnerId] = useState("");
  const [permit, setPermit] = useState(initialPermit);
  const [phase, setPhase] = useState<Phase>("form");
  // Set when the permit is malformed (InvalidFormat — MT was never called); a live
  // format error is derived below (a fully-typed but malformed permit). A permit MT
  // rejected as not-active is an apiAlert instead, not a format error.
  const [serverPermitError, setServerPermitError] = useState(false);
  const [apiAlert, setApiAlert] = useState<ApiAlert>(null);
  const [result, setResult] = useState<PropertyVerificationResult | null>(null);

  const identityMut = verifyIdentityMutation();
  const propertyMut = verifyPropertyMutation(propertyId ?? 0);

  // Whether the ID field is shown: no roster owner, or the host picked "another owner".
  const showIdField = !hasSaved || mode === "newOwner";
  const permitValid = PERMIT_RE.test(permit);
  const idValid = !showIdField || digitsOnly(ownerId).length > 0;
  const canVerify = permitValid && idValid && phase !== "loading";
  // Live format error: fully typed (8 digits) but malformed, OR MT said invalid.
  const permitError = serverPermitError || (permit.length === 8 && !permitValid);

  const report = (payload: VerificationPayload | null) =>
    onVerificationChange?.(payload);

  // Reset to unverified whenever the inputs/identity change.
  useEffect(() => {
    if (phase === "success") return;
    report(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permit, ownerId, mode, phase]);

  const buildPayload = (): VerificationPayload => ({
    permitNumber: permit,
    ...(hasSaved && mode === "sameId"
      ? { ownerIdentityId: savedOwner.id }
      : { idNumber: digitsOnly(ownerId) }),
  });

  const handleVerify = async () => {
    setServerPermitError(false);
    setApiAlert(null);
    if (!permitValid) {
      setServerPermitError(true);
      return;
    }
    const body = {
      permitNumber: permit,
      ...(hasSaved && mode === "sameId"
        ? { ownerIdentityId: savedOwner.id }
        : { idNumber: digitsOnly(ownerId) }),
    };
    setPhase("loading");
    try {
      const res =
        surface === "dashboard" && propertyId
          ? await propertyMut.mutateAsync(body)
          : await identityMut.mutateAsync(body);
      setResult(res);
      switch (res.state) {
        case VerificationState.Success:
          if (!suppressSuccessView) setPhase("success");
          report(buildPayload());
          onVerified?.(res);
          break;
        case VerificationState.ServiceUnavailable:
          setPhase("form");
          setApiAlert("service");
          report(buildPayload()); // saved & pending — backend retries at create
          break;
        case VerificationState.NoMatch:
          setPhase("form");
          setApiAlert("noMatch");
          break;
        // The permit parsed fine and MT answered — it just isn't usable (expired,
        // cancelled, or unknown to MT). A format hint would be wrong here: the
        // number the host typed is well-formed, so they'd retype it forever.
        case VerificationState.InvalidPermit:
          setPhase("form");
          setApiAlert("inactivePermit");
          break;
        case VerificationState.InvalidFormat:
        default:
          setPhase("form");
          setServerPermitError(true);
          break;
      }
    } catch {
      setPhase("form");
      setApiAlert("service");
      report(buildPayload());
    }
  };

  const handleEdit = () => {
    setPhase("form");
    setApiAlert(null);
    report(null);
  };

  const successMask = useMemo(() => {
    if (hasSaved && mode === "sameId") return savedOwner?.maskedNationalId ?? "";
    return maskLocal(digitsOnly(ownerId));
  }, [hasSaved, mode, savedOwner, ownerId]);

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: C.input,
      borderRadius: "12px",
      "& fieldset": { borderColor: C.border },
    },
  };
  const labelSx = { fontSize: 14, fontWeight: 700, color: C.label, mb: 1, textAlign: "start" as const };

  return (
    <Box sx={{ backgroundColor: C.card, borderRadius: "16px", p: "20px", direction: "inherit" }}>
      {/* Header: title + subtitle (start) · optional tag (end) */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: "16px" }}>
        <Box sx={{ textAlign: "start" }}>
          <Typography sx={{ fontSize: 18, fontWeight: 800, color: C.label }}>
            {t("PERMIT_VERIFICATION.TITLE")}
          </Typography>
          <Typography sx={{ fontSize: 13, color: C.muted, mt: 0.5 }}>
            {phase === "success"
              ? t("PERMIT_VERIFICATION.SUBTITLE_SUCCESS")
              : t("PERMIT_VERIFICATION.SUBTITLE")}
          </Typography>
        </Box>
        <Box
          sx={{
            flexShrink: 0,
            backgroundColor: C.tagBg,
            color: C.primary,
            fontSize: 11,
            fontWeight: 700,
            borderRadius: "100px",
            px: "12px",
            py: "6px",
            whiteSpace: "nowrap",
          }}
        >
          {t("PERMIT_VERIFICATION.TAG_OPTIONAL")}
        </Box>
      </Box>

      {phase === "success" && result ? (
        /* ---- Success block ---- */
        <Box sx={{ backgroundColor: C.successBg, border: `1px solid ${C.successBorder}`, borderRadius: "16px", p: "20px", textAlign: "start" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: "12px" }}>
            <CheckCircleIcon sx={{ color: C.successText, fontSize: 22 }} />
            <Typography sx={{ color: C.successText, fontWeight: 800, fontSize: 15 }}>
              {t("PERMIT_VERIFICATION.SUCCESS_BADGE")}
            </Typography>
          </Box>
          <Typography sx={{ fontWeight: 800, fontSize: 18, color: C.label, mb: 1 }}>
            {t("PERMIT_VERIFICATION.OWNER_NAME")} {result.verifiedNameAr || result.verifiedNameEn}
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#374151", lineHeight: 2 }}>
            {t("PERMIT_VERIFICATION.PERMIT_NUMBER")} {permit}
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#374151", lineHeight: 2 }}>
            {t("PERMIT_VERIFICATION.OWNER_ID")} {successMask}
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#374151", lineHeight: 2 }}>
            {t("PERMIT_VERIFICATION.VALID_UNTIL")}{" "}
            {result.expiryDate ? result.expiryDate.slice(0, 10) : ""}
          </Typography>
          <Typography
            onClick={handleEdit}
            sx={{ color: C.primary, fontWeight: 700, fontSize: 14, mt: "12px", cursor: "pointer" }}
          >
            {t("PERMIT_VERIFICATION.EDIT_DATA")}
          </Typography>
        </Box>
      ) : (
        /* ---- Form (idle / filled / loading / errors) ---- */
        <>
          {apiAlert === "noMatch" && (
            <Box sx={{ backgroundColor: C.errorBg, borderRadius: "12px", p: "14px", mb: "16px" }}>
              <Typography sx={{ color: C.errorText, fontSize: 13, textAlign: "center", lineHeight: 1.7 }}>
                {t("PERMIT_VERIFICATION.ERROR_NO_MATCH")}
                {surface === "dashboard" && " " + t("PERMIT_VERIFICATION.ERROR_NO_MATCH_DASHBOARD")}
              </Typography>
            </Box>
          )}
          {apiAlert === "inactivePermit" && (
            <Box sx={{ backgroundColor: C.errorBg, borderRadius: "12px", p: "14px", mb: "16px" }}>
              <Typography sx={{ color: C.errorText, fontSize: 13, textAlign: "center", lineHeight: 1.7 }}>
                {/* MT returns the expiry only when the permit exists but lapsed; error
                    code 111 (unknown permit) comes back bare, hence the fallback. */}
                {result?.expiryDate
                  ? t("PERMIT_VERIFICATION.ERROR_PERMIT_EXPIRED", {
                      date: result.expiryDate.slice(0, 10),
                    })
                  : t("PERMIT_VERIFICATION.ERROR_PERMIT_INACTIVE")}
              </Typography>
            </Box>
          )}
          {apiAlert === "service" && (
            <Box sx={{ backgroundColor: C.serviceBg, borderRadius: "12px", p: "14px", mb: "16px" }}>
              <Typography sx={{ color: C.serviceText, fontSize: 13, textAlign: "center", lineHeight: 1.7 }}>
                {t("PERMIT_VERIFICATION.ERROR_SERVICE")}
              </Typography>
            </Box>
          )}

          {/* Saved-ID radio (only when the host has a roster owner) */}
          {hasSaved && (
            <Box sx={{ mb: "16px" }}>
              <Typography sx={labelSx}>{t("PERMIT_VERIFICATION.SAVED_ID_QUESTION")}</Typography>
              <RadioGroup
                value={mode}
                onChange={(e) => setMode(e.target.value as "sameId" | "newOwner")}
              >
                <FormControlLabel
                  value="sameId"
                  control={<Radio sx={{ color: C.primary, "&.Mui-checked": { color: C.primary } }} />}
                  label={t("PERMIT_VERIFICATION.SAME_ID", {
                    mask: savedOwner?.maskedNationalId,
                    defaultValue: "نعم، نفس الهوية ({{mask}})",
                  })}
                />
                <FormControlLabel
                  value="newOwner"
                  control={<Radio sx={{ color: C.primary, "&.Mui-checked": { color: C.primary } }} />}
                  label={t("PERMIT_VERIFICATION.OTHER_OWNER")}
                />
              </RadioGroup>
            </Box>
          )}

          {showIdField && (
            <Box sx={{ mb: "16px" }}>
              <Typography sx={labelSx}>{t("PERMIT_VERIFICATION.OWNER_ID_LABEL")}</Typography>
              <TextField
                fullWidth
                size="small"
                value={ownerId}
                onChange={(e) => setOwnerId(digitsOnly(e.target.value))}
                inputProps={{ inputMode: "numeric" }}
                disabled={phase === "loading"}
                sx={inputSx}
              />
            </Box>
          )}

          <Box sx={{ mb: "16px" }}>
            <Typography sx={labelSx}>{t("PERMIT_VERIFICATION.PERMIT_LABEL")}</Typography>
            <TextField
              fullWidth
              size="small"
              value={permit}
              onChange={(e) => {
                setPermit(digitsOnly(e.target.value).slice(0, 8));
                if (serverPermitError) setServerPermitError(false);
              }}
              inputProps={{ inputMode: "numeric", maxLength: 8 }}
              disabled={phase === "loading"}
              error={permitError}
              sx={{
                ...inputSx,
                ...(permitError && {
                  "& .MuiOutlinedInput-root fieldset": { borderColor: `${C.errorBorder} !important`, borderWidth: "1.5px" },
                }),
              }}
            />
            {permitError && (
              <Typography sx={{ color: C.errorText, fontSize: 12, mt: 1, textAlign: "start" }}>
                {t("PERMIT_VERIFICATION.ERROR_PERMIT_FORMAT")}
              </Typography>
            )}
          </Box>

          {/* Verify button — outline/disabled until valid, solid when enabled */}
          <Box
            component="button"
            type="button"
            disabled={!canVerify}
            onClick={handleVerify}
            sx={{
              width: "100%",
              height: 48,
              borderRadius: "12px",
              border: `1.5px solid ${C.primary}`,
              backgroundColor: canVerify ? C.primary : "transparent",
              color: canVerify ? "#FFFFFF" : C.primary,
              fontWeight: 700,
              fontSize: 15,
              cursor: canVerify ? "pointer" : "not-allowed",
              opacity: canVerify ? 1 : 0.55,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              transition: "all 0.15s",
            }}
          >
            {phase === "loading" ? (
              <>
                {t("PERMIT_VERIFICATION.VERIFYING")}
                <CircularProgress size={16} sx={{ color: "#FFFFFF" }} />
              </>
            ) : (
              t("PERMIT_VERIFICATION.VERIFY_NOW")
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default VerificationCard;
